import $ from 'jquery';
import { debounce, find, findLast } from 'lodash';
import UniScroll from '../_shared/uni-scroll';

/**
 * @desc Implements a horizontal card slider, requires fixed-width cards,
 * utilities native overflow-x behavior.
 */
export default class CardSlider {
  /**
   * @param {Object} options
   * @param {null|HTMLElement} options.sliderEl
   * @param {string} options.sliderSelector
   * @param {string} options.listSelector
   * @param {string} options.itemsSelector
   * @param {string} options.navSelector
   * @param {string} options.buttonPrevSelector
   * @param {string} options.buttonNextSelector
   */
  constructor(
    {
      sliderEl = null,
      sliderSelector = '.js-card-slider',
      listSelector = '.js-card-slider-list',
      itemsSelector = '.js-card-slider-list-item',
      navSelector = '.js-card-slider-nav',
      buttonPrevSelector = '.js-card-slider-nav-prev',
      buttonNextSelector = '.js-card-slider-nav-next',
      dataDirKey = 'dir',
    } = {},
  ) {
    this.VISIBLE = 'visible';
    this.NOTICEABLE = 'noticeable';
    this.INVISIBLE = 'invisible';

    this.$slider = $(sliderEl || sliderSelector);
    this.$list = this.$slider.find(listSelector);
    this.$items = this.$list.find(itemsSelector);
    this.$nav = this.$slider.find(navSelector);
    this.isRtl = String(this.$slider.data(dataDirKey)).toLocaleLowerCase() === 'rtl';
    this.$buttonPrev = this.$nav.find(buttonPrevSelector);
    this.$buttonNext = this.$nav.find(buttonNextSelector);

    this.uniScroll = new UniScroll();

    this.init();
  }

  init() {
    this.isInitialized = true;

    if (!this.$list.length || !this.$items.length || !this.$buttonPrev.length || !this.$buttonNext.length) {
      this.isInitialized = false;

      return;
    }

    this.items = this.$items.toArray();

    this.calculate();

    this.debouncedCalculate = debounce(() => this.calculate(), 150);

    this.attachEventListeners();

    this.dragAndSlide();
  }

  /**
   * @desc Calculates the distance in pixels to the left corner
   * of next invisible card (nextItemOffset), and offset in pixels to the left corner
   * of the last invisible card in a row (previousItemOffset). Also, disables/enables
   * control buttons or completely hides them if both buttons are disabled.
   * Turns overflow-x ON and OFF depending on whether we need sliding or not.
   */
  calculate() {
    this.previousItemOffset = this.isRtl ? this.getNextItemOffset() : this.getPreviousItemOffset();
    this.nextItemOffset = this.isRtl ? this.getPreviousItemOffset() : this.getNextItemOffset();

    const isFirstCardVisible = this.isRtl ? this.checkLastCardVisibility() : this.checkFirstCardVisibility();
    const isLastCardVisible = this.isRtl ? this.checkFirstCardVisibility() : this.checkLastCardVisibility();

    this.$buttonPrev.prop('disabled', isFirstCardVisible);
    this.$buttonNext.prop('disabled', isLastCardVisible);

    if (isFirstCardVisible) {
      this.$slider.addClass('is-scrolled-max-left');
    } else {
      this.$slider.removeClass('is-scrolled-max-left');
    }

    if (isLastCardVisible) {
      this.$slider.addClass('is-scrolled-max-right');
    } else {
      this.$slider.removeClass('is-scrolled-max-right');
    }

    if (isFirstCardVisible && isLastCardVisible) {
      this.$list.addClass('no-scroll');
      this.$nav.addClass('nav-hidden');
    } else {
      this.$list.removeClass('no-scroll');
      this.$nav.removeClass('nav-hidden');
    }
  }

  /**
   * @desc Returns distance in pixels to the left corner of the last
   * invisible card in a row (previousItemOffset)
   * @returns {number}
   */
  getPreviousItemOffset() {
    let card = null;

    const numberOfVisibleCards = this.items.reduce(
      (counter, item) => (this.checkCardVisibility(item) === this.VISIBLE ? counter + 1 : counter), 0,
    );

    for (let i = 0; i < this.items.length; i += 1) {
      const currentCardVisibility = this.checkCardVisibility(this.items[i]);

      if (currentCardVisibility === this.VISIBLE) {
        const cardIndex = this.isRtl
          ? i + 2 + (numberOfVisibleCards > 1 ? numberOfVisibleCards : 0)
          : i - 2 - (numberOfVisibleCards > 1 ? numberOfVisibleCards : 0);
        card = this.items[cardIndex];

        break;
      } else if (currentCardVisibility === this.NOTICEABLE) {
        const cardIndex = this.isRtl
          ? i + 3 + (numberOfVisibleCards > 1 ? numberOfVisibleCards : 0)
          : i - 1 - (numberOfVisibleCards > 1 ? numberOfVisibleCards : 0);
        card = this.items[cardIndex];

        break;
      }
    }

    if (!card) {
      return 0;
    }

    if (this.isRtl) {
      return this.$list[0].scrollWidth - Math.abs(card.offsetLeft - this.$list.width()) + $(card).width();
    }

    return card.offsetLeft + $(card).width();
  }

  /**
   * @desc Returns distance in pixels to the left corner of next invisible (or semi-visible) card.
   * @returns {number}
   */
  getNextItemOffset() {
    const lastFullyVisibleCard = findLast(this.items, (item) => this.checkCardVisibility(item) === this.VISIBLE)
      || find(this.items, (item) => this.checkCardVisibility(item) === this.NOTICEABLE);

    if (this.isRtl) {
      return (
        this.$list[0].scrollWidth
        - Math.abs(lastFullyVisibleCard.offsetLeft - this.$list.width())
        + $(lastFullyVisibleCard).width()
      );
    }

    return lastFullyVisibleCard.offsetLeft + $(lastFullyVisibleCard).width();
  }

  /**
   * @desc Checks whether a first card is visible or not.
   * @return {boolean}
   */
  checkFirstCardVisibility() {
    return this.checkCardVisibility(this.items[0]) === this.VISIBLE;
  }

  /**
   * @desc Checks whether a last card is visible or not.
   * @return {boolean}
   */
  checkLastCardVisibility() {
    return this.checkCardVisibility(this.items[this.items.length - 1]) === this.VISIBLE;
  }

  /**
   * @desc Checks visibility of a card, it can be visible,
   * partially-visible (noticeable), and invisible.
   * @param {HTMLElement} card
   * @returns {string|null}
   */
  checkCardVisibility(card) {
    if (!card) {
      return null;
    }

    let viewPortLeftBoundary;
    let viewPortRightBoundary;
    let cardLeftBoundary;
    let cardRightBoundary;
    let isFullyVisible;
    let isPartiallyVisible;

    if (this.isRtl) {
      viewPortLeftBoundary = this.$list[0].scrollWidth - this.uniScroll.scrollLeft(this.$list[0]);
      viewPortRightBoundary = Math.abs(
        this.uniScroll.scrollLeft(this.$list[0]) + this.$list.width() - this.$list[0].scrollWidth,
      );
      cardLeftBoundary = this.$list.width() - $(card)[0].offsetLeft;
      cardRightBoundary = this.$list.width() - ($(card)[0].offsetLeft + $(card).width());

      isFullyVisible = cardLeftBoundary < viewPortLeftBoundary && cardRightBoundary > viewPortRightBoundary;
      isPartiallyVisible = !isFullyVisible
        && ((cardLeftBoundary < viewPortLeftBoundary && cardLeftBoundary > viewPortRightBoundary)
          || (cardRightBoundary < viewPortLeftBoundary && cardRightBoundary > viewPortRightBoundary));
    } else {
      viewPortLeftBoundary = this.$list.scrollLeft();
      viewPortRightBoundary = this.$list.scrollLeft() + this.$list.width();
      cardLeftBoundary = $(card)[0].offsetLeft;
      cardRightBoundary = $(card)[0].offsetLeft + $(card).width();

      isFullyVisible = (cardLeftBoundary >= viewPortLeftBoundary - 1
        && cardRightBoundary <= viewPortRightBoundary + 1)

        // exceptional case for wide width cards
        || ($(card).width() >= this.$list.width()
          && (
            (cardLeftBoundary > viewPortLeftBoundary && (
              cardRightBoundary >= viewPortRightBoundary
              && cardRightBoundary < viewPortRightBoundary + $(card).width()
            ))
            || (cardRightBoundary < viewPortRightBoundary && (
              cardLeftBoundary <= viewPortLeftBoundary
              && cardLeftBoundary > viewPortLeftBoundary - $(card).width()
            ))
          ));

      isPartiallyVisible = !isFullyVisible
        && ((cardLeftBoundary > viewPortLeftBoundary && cardLeftBoundary < viewPortRightBoundary)
          || (cardRightBoundary > viewPortLeftBoundary && cardRightBoundary < viewPortRightBoundary));
    }

    if (isFullyVisible) {
      return this.VISIBLE;
    }

    if (isPartiallyVisible) {
      return this.NOTICEABLE;
    }

    return this.INVISIBLE;
  }

  /**
   * @desc Add event listeners to slider's controls.
   */
  attachEventListeners() {
    this.$list.scroll(this.debouncedCalculate);

    $(window).resize(this.debouncedCalculate);

    this.$buttonPrev.on('click', () => {
      const left = this.isRtl
        ? this.uniScroll.getNormalizedOffset(this.$list[0], this.nextItemOffset)
        : this.previousItemOffset;

      this.$list.get(0).scroll({
        top: 0,
        left,
        behavior: window.requestAnimationFrame ? 'smooth' : 'auto',
      });
    });

    this.$buttonNext.on('click', () => {
      const left = this.isRtl
        ? this.uniScroll.getNormalizedOffset(this.$list[0], this.previousItemOffset)
        : this.nextItemOffset;

      this.$list.get(0).scroll({
        top: 0,
        left,
        behavior: window.requestAnimationFrame ? 'smooth' : 'auto',
      });
    });
  }

  dragAndSlide() {
    const slider = this.$list;
    if (!slider.hasClass('draggable')) {
      return;
    }
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.on('mousedown', (e) => {
      isDown = true;
      slider.addClass('active');
      startX = e.pageX - slider.offset().left;
      scrollLeft = slider.scrollLeft();
    });
    slider.on('mouseleave', () => {
      isDown = false;
      slider.removeClass('active');
    });
    slider.on('mouseup', () => {
      isDown = false;
      slider.removeClass('active');
    });
    slider.on('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offset().left;
      const walk = (x - startX) * 3; // scroll-fast
      slider.scrollLeft(scrollLeft - walk);
    });
  }
}
